import { defineStore } from 'pinia';
import { collection, getDocs, getDoc, doc, addDoc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { dispatchNotification } from '~/lib/notificationDispatch';
import type { Task, TaskClientResponseMessage } from '~/types/models';
import { stripUndefined } from '~/lib/stripUndefined';
import { resolveWorkspaceEntitlements } from '~/lib/subscriptionPlans';
import { getTenantTaskStatuses } from '~/lib/taskConfig';
import { runFreshRequest, type FreshRequestOptions } from '~/lib/requestFreshness';
import { buildClientPortalDestination, resolveClientEmailOrigin } from '~/lib/tenantLinks';

export const useTaskStore = defineStore('tasks', {
    state: () => ({
        tasks: [] as Task[],
        taskError: null as string | null,
        dataTenantId: null as string | null,
        loadedForTenantId: null as string | null,
        loadedProjectIds: [] as string[],
    }),

    actions: {
        /**
         * Gets the current tenant ID from the tenant store
         * @private
         */
        _getTenantId(): string {
            const tenantStore = useTenantStore();
            if (!tenantStore.currentTenantId) {
                throw new Error('No tenant selected. Please log in.');
            }
            return tenantStore.currentTenantId;
        },

        _getConnectedTaskIds(taskId: string): string[] {
            const taskMap = new Map(this.tasks.map(task => [task.id, task] as const));
            const childrenByParent = new Map<string, string[]>();

            for (const task of this.tasks) {
                const parentId = String(task.subtaskOfId || '').trim();
                if (!parentId) continue;
                const siblings = childrenByParent.get(parentId) ?? [];
                siblings.push(task.id);
                childrenByParent.set(parentId, siblings);
            }

            const visited = new Set<string>();
            const queue = [taskId];

            while (queue.length > 0) {
                const currentId = queue.shift();
                if (!currentId || visited.has(currentId)) continue;

                visited.add(currentId);

                const currentTask = taskMap.get(currentId);
                const parentId = String(currentTask?.subtaskOfId || '').trim();
                if (parentId && !visited.has(parentId)) {
                    queue.push(parentId);
                }

                const childIds = childrenByParent.get(currentId) ?? [];
                for (const childId of childIds) {
                    if (!visited.has(childId)) {
                        queue.push(childId);
                    }
                }
            }

            return Array.from(visited);
        },

        _assignedUserIds(task: Partial<Task>): string[] {
            return Array.from(new Set(
                [
                    ...(Array.isArray(task.assigneeIds) ? task.assigneeIds : []),
                    task.assigneeId,
                ]
                    .map((userId) => String(userId || '').trim())
                    .filter(Boolean),
            ));
        },

        async _dispatchTaskAssignedNotification(taskId: string, task: Partial<Task>, recipientIds?: string[]): Promise<void> {
            const tenantId = this._getTenantId();
            const assigneeIds = Array.from(new Set(
                (recipientIds || this._assignedUserIds(task))
                    .map((userId) => String(userId || '').trim())
                    .filter(Boolean),
            ));

            if (!assigneeIds.length) {
                return;
            }

            const { tenantPath } = useTenantPath();
            const userStore = useUserStore();

            await dispatchNotification({
                tenantId,
                surface: 'tenant',
                eventType: 'task_assigned',
                title: task.title ? `Task assigned: ${task.title}` : 'New task assigned',
                body: task.projectId
                    ? 'A project task has been assigned to you.'
                    : 'A workspace task has been assigned to you.',
                recipients: [{ type: 'tenant_user_ids', userIds: assigneeIds }],
                entityType: 'task',
                entityId: taskId,
                projectId: task.projectId,
                clientId: task.clientId,
                actionPath: tenantPath({ path: '/tasks', query: { taskId } }),
                actionLabel: 'Open task',
                actorId: userStore.user?.id,
                actorName: userStore.user?.displayName || userStore.user?.fullName,
                actorAvatarURL: userStore.user?.avatarURL,
            });
        },

        async _resolveClientSlugForTask(task: Partial<Task>): Promise<string | null> {
            const clientId = String(task.clientId || '').trim();
            if (!clientId) {
                return null;
            }

            const clientStore = useClientStore();
            await clientStore.fetchClients();

            return clientStore.clients.find((client) => client.id === clientId)?.slug || null;
        },

        _buildClientTaskPortalDestination(taskId: string, clientSlug: string) {
            const tenantStore = useTenantStore();
            const config = useRuntimeConfig();

            return buildClientPortalDestination({
                origin: resolveClientEmailOrigin({
                    tenant: tenantStore.currentTenant,
                    appDomain: config.public.appDomain,
                    fallbackOrigin: window.location.origin,
                }),
                tenantId: this._getTenantId(),
                clientSlug,
                section: 'tasks',
                focusParam: 'focusTaskId',
                focusValue: taskId,
            });
        },

        async _sendClientTaskInboxMessage(
            taskId: string,
            task: Partial<Task>,
            clientSlug: string,
        ): Promise<void> {
            const clientId = String(task.clientId || '').trim();
            if (!clientId || !clientSlug) return;

            const clientStore = useClientStore();
            const client = clientStore.clients.find((entry) => entry.id === clientId)
                || await clientStore.fetchClientById(clientId);
            const { actionPath } = this._buildClientTaskPortalDestination(taskId, clientSlug);

            await useMessagesStore().sendAutomatedMessage({
                clientId,
                clientName: client?.name,
                subject: task.title ? `Task response requested: ${task.title}` : 'Task response requested',
                content: task.title
                    ? `The team has requested a response on “${task.title}”. Open the task to review the details and reply.`
                    : 'The team has requested a response on a shared task. Open it to review the details and reply.',
                actionPath,
                actionLabel: 'Open task request',
                entityType: 'task',
                entityId: taskId,
            });
        },

        async _buildClientTaskResponseEmail(task: Partial<Task>, actionLink: string) {
            const clientId = String(task.clientId || '').trim();
            const clientStore = useClientStore();
            const client = clientId
                ? clientStore.clients.find((entry) => entry.id === clientId) || await clientStore.fetchClientById(clientId)
                : null;
            const projectStore = useProjectStore();
            const projectId = String(task.projectId || '').trim();
            const project = projectId
                ? projectStore.projects.find((entry) => entry.id === projectId) || await projectStore.fetchProjectById(projectId)
                : null;
            const tenantStore = useTenantStore();

            return {
                template: 'task_response_requested' as const,
                workspaceName: tenantStore.currentTenant?.name || 'Vuey',
                clientName: client?.name,
                projectName: project?.name,
                projectSummary: 'Open the task to review what the team needs and reply.',
                documentTitle: task.title || 'Shared task',
                actionLink,
                actionLabel: 'Open task request',
            };
        },

        async _dispatchClientActionRequiredNotification(taskId: string, task: Partial<Task>): Promise<void> {
            if (task.clientVisible !== true || task.clientResponseRequired !== true) {
                return;
            }

            const tenantId = this._getTenantId();
            const clientId = String(task.clientId || '').trim();
            if (!clientId) {
                return;
            }

            const clientSlug = await this._resolveClientSlugForTask(task);
            if (!clientSlug) {
                return;
            }
            const destination = this._buildClientTaskPortalDestination(taskId, clientSlug);
            const email = await this._buildClientTaskResponseEmail(task, destination.actionLink);

            await Promise.allSettled([
                dispatchNotification({
                    tenantId,
                    surface: 'client',
                    eventType: 'task_action_required',
                    title: task.title ? `Action requested: ${task.title}` : 'Task action required',
                    body: 'The team needs a response, file, or decision from you on a shared task.',
                    recipients: [{
                        type: 'portal_access',
                        clientId,
                        projectId: task.projectId,
                        permission: 'viewTasks',
                    }],
                    severity: 'warning',
                    clientId,
                    projectId: task.projectId,
                    entityType: 'task',
                    entityId: taskId,
                    actionPath: destination.actionPath,
                    actionLabel: 'Open task request',
                    dedupeKey: `task_action_required:${taskId}`,
                    dedupeWindowHours: 24,
                    email,
                }),
                this._sendClientTaskInboxMessage(taskId, task, clientSlug),
            ]);
        },

        async _syncClientVisibilityAcrossHierarchy(taskId: string, clientVisible: boolean): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            const relatedTaskIds = this._getConnectedTaskIds(taskId);
            if (!relatedTaskIds.length) return;

            const updates = relatedTaskIds
                .map((relatedTaskId) => {
                    const existingTask = this.tasks.find(task => task.id === relatedTaskId);
                    if (!existingTask) return null;

                    const shouldDisableClientResponse = clientVisible !== true && existingTask.clientResponseRequired === true;
                    const alreadyMatches = existingTask.clientVisible === clientVisible && !shouldDisableClientResponse;
                    if (alreadyMatches) return null;

                    return {
                        id: relatedTaskId,
                        data: stripUndefined({
                            clientVisible,
                            clientResponseRequired: clientVisible === true ? existingTask.clientResponseRequired : false,
                            updatedAt: new Date(),
                        } satisfies Partial<Task>),
                    };
                })
                .filter((entry): entry is { id: string, data: Partial<Task> } => entry !== null);

            if (!updates.length) return;

            await Promise.all(updates.map(async ({ id, data }) => {
                await updateDoc(doc($db, `tenants/${tenantId}/tasks/${id}`), data);
            }));

            this.tasks = this.tasks.map((task) => {
                const nextUpdate = updates.find(entry => entry.id === task.id);
                return nextUpdate ? { ...task, ...nextUpdate.data } as Task : task;
            });
        },

        /**
         * Fetches all tasks for the current tenant from Firestore
         * 
         * @async
         * @returns {Promise<void>}
         * @throws Logs error to the console if the Firestore Query fails
         * 
         * @example
         * await taskStore.fetchTasks();
         */
        async fetchTasks(options: FreshRequestOptions = {}): Promise<void> {
            const { $db } = useNuxtApp();
            this.taskError = null;

            try {
                const tenantId = this._getTenantId();
                if (this.dataTenantId && this.dataTenantId !== tenantId) {
                    this.tasks = [];
                    this.loadedForTenantId = null;
                    this.loadedProjectIds = [];
                }
                await runFreshRequest(`tasks:${tenantId}:all`, async () => {
                    const collectionRef = collection($db, `tenants/${tenantId}/tasks`);
                    const snap = await getDocs(collectionRef);
                    this.tasks = snap.docs.map(taskDoc => ({
                        id: taskDoc.id,
                        ...taskDoc.data()
                    } as Task));
                    this.loadedForTenantId = tenantId;
                    this.dataTenantId = tenantId;
                    this.loadedProjectIds = [];
                }, {
                    ...options,
                    hasData: this.loadedForTenantId === tenantId,
                });
            } catch (error: any) {
                this.taskError = error.message;
                console.error('Error fetching tasks:', error);
            }
        },

        /**
         * Fetches a single task by ID
         * 
         * @param taskId - The ID of the task to fetch
         * @returns The task or null if not found
         */
        async fetchTaskById(taskId: string): Promise<Task | null> {
            const tenantId = this._getTenantId();
            if (this.dataTenantId && this.dataTenantId !== tenantId) {
                this.tasks = [];
                this.loadedForTenantId = null;
                this.loadedProjectIds = [];
            }
            const cached = this.dataTenantId === tenantId
                ? this.tasks.find(task => task.id === taskId)
                : null;
            if (cached) return cached;

            const { $db } = useNuxtApp();
            this.taskError = null;

            try {
                const docRef = doc($db, `tenants/${tenantId}/tasks`, taskId);
                const snap = await getDoc(docRef);
                
                if (!snap.exists()) {
                    return null;
                }
                
                const task = { id: snap.id, ...snap.data() } as Task;
                this.dataTenantId = tenantId;
                this.tasks = [...this.tasks.filter(item => item.id !== task.id), task];
                return task;
            } catch (error: any) {
                this.taskError = error.message;
                console.error('Error fetching task:', error);
                return null;
            }
        },


        /**
         * Fetches all tasks for a specific project
         * 
         * @param projectId - The ID of the project
         * @returns {Promise<void>}
         */
        async fetchTasksByProjectId(projectId: string, options: FreshRequestOptions = {}): Promise<void> {
            const { $db } = useNuxtApp();
            this.taskError = null;

            try {
                const tenantId = this._getTenantId();
                if (this.dataTenantId && this.dataTenantId !== tenantId) {
                    this.tasks = [];
                    this.loadedForTenantId = null;
                    this.loadedProjectIds = [];
                }
                if (!options.force && this.loadedForTenantId === tenantId) {
                    return;
                }

                await runFreshRequest(`tasks:${tenantId}:project:${projectId}`, async () => {
                    const collectionRef = collection($db, `tenants/${tenantId}/tasks`);
                    const q = query(collectionRef, where('projectId', '==', projectId));
                    const snap = await getDocs(q);
                    const projectTasks = snap.docs.map(taskDoc => ({
                        id: taskDoc.id,
                        ...taskDoc.data()
                    } as Task));
                    const projectTaskIds = new Set(projectTasks.map(task => task.id));
                    this.tasks = [
                        ...this.tasks.filter(task => task.projectId !== projectId && !projectTaskIds.has(task.id)),
                        ...projectTasks,
                    ];
                    if (!this.loadedProjectIds.includes(projectId)) {
                        this.loadedProjectIds = [...this.loadedProjectIds, projectId];
                    }
                    this.dataTenantId = tenantId;
                }, {
                    ...options,
                    hasData: this.loadedProjectIds.includes(projectId),
                });
            } catch (error: any) {
                this.taskError = `Error fetching tasks: ${error.message}`;
                console.error('Error fetching tasks by project:', error);
            }
        },

        _resolveCompletedTaskStatus(): string {
            const tenantStore = useTenantStore();
            const configuredStatuses = getTenantTaskStatuses(tenantStore.currentTenant);
            const explicitDone = configuredStatuses.find((status) => ['done', 'complete', 'completed'].includes(String(status.value || '').trim().toLowerCase())
                || ['done', 'complete', 'completed'].includes(String(status.label || '').trim().toLowerCase()));
            return explicitDone?.value || explicitDone?.label || 'Done';
        },

        async markProjectTasksCompleted(projectId: string): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            const completedStatus = this._resolveCompletedTaskStatus();
            await this.fetchTasksByProjectId(projectId, { force: true });
            const projectTasks = this.tasks.filter(task => task.projectId === projectId);

            const tasksToUpdate = projectTasks.filter(task => !['done', 'complete', 'completed'].includes(String(task.status || '').trim().toLowerCase()));
            if (tasksToUpdate.length === 0) {
                return;
            }

            const now = new Date();
            await Promise.all(tasksToUpdate.map(async (task) => {
                await updateDoc(doc($db, `tenants/${tenantId}/tasks/${task.id}`), {
                    status: completedStatus,
                    updatedAt: now,
                });
            }));

            this.tasks = this.tasks.map((task) => (
                task.projectId === projectId && !['done', 'complete', 'completed'].includes(String(task.status || '').trim().toLowerCase())
                    ? { ...task, status: completedStatus, updatedAt: now } as Task
                    : task
            ));

        },

        async requestClientTaskFollowUp(taskId: string, message: string): Promise<Task | null> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            const trimmedMessage = String(message || '').trim();
            if (!trimmedMessage) {
                throw new Error('Add the information you need from the client first.');
            }

            let currentTask = this.tasks.find(task => task.id === taskId) || null;
            if (!currentTask) {
                currentTask = await this.fetchTaskById(taskId);
            }

            if (!currentTask) {
                throw new Error('Task not found.');
            }

            const userStore = useUserStore();
            const now = new Date();
            const nextMessage = stripUndefined({
                id: crypto.randomUUID(),
                authorType: 'tenant',
                authorId: userStore.user?.id,
                authorName: userStore.user?.displayName || userStore.user?.fullName || 'Team',
                body: trimmedMessage,
                createdAt: now,
            }) as TaskClientResponseMessage;
            const nextThread = [...(Array.isArray(currentTask.clientResponseThread) ? currentTask.clientResponseThread : []), nextMessage];
            const nextTaskPatch = stripUndefined({
                clientVisible: true,
                clientResponseRequired: true,
                clientResponseStatus: 'waiting' as const,
                clientResponsePrompt: trimmedMessage,
                clientResponseThread: nextThread,
                clientResponseRequestedAt: now,
                clientResponseReminderStage: 0 as const,
                updatedAt: now,
            });

            await updateDoc(doc($db, `tenants/${tenantId}/tasks/${taskId}`), nextTaskPatch);

            let updatedTask: Task | null = null;
            this.tasks = this.tasks.map((task) => (
                task.id === taskId
                    ? (updatedTask = { ...task, ...nextTaskPatch } as Task)
                    : task
            ));
            if (!updatedTask) {
                updatedTask = { ...currentTask, ...nextTaskPatch } as Task;
            }

            try {
                const clientSlug = await this._resolveClientSlugForTask(currentTask);
                const clientId = String(currentTask.clientId || '').trim();
                if (clientSlug && clientId) {
                    const destination = this._buildClientTaskPortalDestination(taskId, clientSlug);
                    const email = await this._buildClientTaskResponseEmail(currentTask, destination.actionLink);
                    await Promise.allSettled([
                        dispatchNotification({
                            tenantId,
                            surface: 'client',
                            eventType: 'task_action_required',
                            title: currentTask.title ? `More info needed: ${currentTask.title}` : 'More information requested',
                            body: trimmedMessage,
                            recipients: [{
                                type: 'portal_access',
                                clientId,
                                projectId: currentTask.projectId,
                                permission: 'viewTasks',
                            }],
                            severity: 'warning',
                            clientId,
                            projectId: currentTask.projectId,
                            entityType: 'task',
                            entityId: taskId,
                            actionPath: destination.actionPath,
                            actionLabel: 'Open task request',
                            dedupeKey: `task_action_required:${taskId}:${nextMessage.id}`,
                            dedupeWindowHours: 24,
                            email,
                        }),
                        this._sendClientTaskInboxMessage(taskId, currentTask, clientSlug),
                    ]);
                }
            } catch (notificationError) {
                console.warn('Failed to dispatch follow-up client action notification:', notificationError);
            }

            return updatedTask;
        },

        /**
         * Adds a new task to Firestore
         * 
         * @param taskData - The task data (without id, createdAt, updatedAt)
         * @param userId - The ID of the user creating the task
         * @returns The ID of the created task
         */
        async addTask(
            taskData: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>, 
            userId: string
        ): Promise<string> {
            const { $db } = useNuxtApp();
            this.taskError = null;

            try {
                const tenantId = this._getTenantId();
                const collectionRef = collection($db, `tenants/${tenantId}/tasks`);

                const task = stripUndefined({
                    ...taskData,
                    createdBy: userId,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                });

                const docRef = await addDoc(collectionRef, task);
                
                // Add to local state
                this.tasks.push({ 
                    id: docRef.id, 
                    ...task 
                } as Task);

                if (task.subtaskOfId) {
                    const parentTask = this.tasks.find(existingTask => existingTask.id === task.subtaskOfId);
                    const shouldShareAcrossHierarchy = parentTask?.clientVisible === true || task.clientVisible === true;
                    if (shouldShareAcrossHierarchy) {
                        await this._syncClientVisibilityAcrossHierarchy(docRef.id, true);
                    }
                }
                
                // Log activity
                const userStore = useUserStore();
                const activityStore = useActivityStore();
                try {
                    await activityStore.logActivity({
                        action: 'create',
                        entityType: 'task',
                        entityId: docRef.id,
                        description: `Created task: ${taskData.title}`,
                        userId,
                        userName: userStore.user?.displayName,
                        userAvatar: userStore.user?.avatarURL,
                        changedField: undefined
                    });
                } catch (err) {
                    // Log but don't fail - activity logging is not critical
                    console.warn('Failed to log task creation activity:', err);
                }

                try {
                    await this._dispatchTaskAssignedNotification(docRef.id, task as Partial<Task>);
                } catch (notificationError) {
                    console.warn('Failed to dispatch task assignment notification:', notificationError);
                }

                try {
                    await this._dispatchClientActionRequiredNotification(docRef.id, task as Partial<Task>);
                } catch (notificationError) {
                    console.warn('Failed to dispatch client action notification:', notificationError);
                }

                return docRef.id;
            } catch (error: any) {
                this.taskError = error.message;
                console.error('Error adding task:', error);
                throw error;
            }
        },

        /**
         * Updates an existing task
         * 
         * @param taskId - The ID of the task to update
         * @param data - Partial task data to update
         * @returns {Promise<void>}
         */
        async updateTask(taskId: string, data: Partial<Task>): Promise<void> {
            const { $db } = useNuxtApp();
            this.taskError = null;

            try {
                const tenantId = this._getTenantId();
                const tenantStore = useTenantStore();
                const entitlements = resolveWorkspaceEntitlements(tenantStore.currentTenant);
                if (data.recurring?.enabled && !entitlements.plan.features.recurringTasks) {
                    throw new Error('Recurring tasks are available on the Growth plan and above.');
                }
                const docRef = doc($db, `tenants/${tenantId}/tasks/${taskId}`);
                
                // Get current task to track what changed
                const currentTask = this.tasks.find(t => t.id === taskId);
                const previousAssigneeIds = currentTask ? this._assignedUserIds(currentTask) : [];
                
                const updateData = stripUndefined({
                    ...data,
                    updatedAt: new Date(),
                });
                
                await updateDoc(docRef, updateData);
                
                // Update local state
                const taskIndex = this.tasks.findIndex(task => task.id === taskId);
                if (taskIndex > -1) {
                    this.tasks[taskIndex] = { 
                        ...this.tasks[taskIndex], 
                        ...updateData 
                    } as Task;
                }

                if (Object.prototype.hasOwnProperty.call(data, 'clientVisible') && typeof data.clientVisible === 'boolean') {
                    await this._syncClientVisibilityAcrossHierarchy(taskId, data.clientVisible);
                }
                else if (Object.prototype.hasOwnProperty.call(data, 'subtaskOfId')) {
                    const updatedTask = this.tasks.find(task => task.id === taskId);
                    const parentTask = updatedTask?.subtaskOfId
                        ? this.tasks.find(task => task.id === updatedTask.subtaskOfId)
                        : null;
                    const shouldShareAcrossHierarchy = updatedTask?.clientVisible === true || parentTask?.clientVisible === true;
                    if (updatedTask?.subtaskOfId && shouldShareAcrossHierarchy) {
                        await this._syncClientVisibilityAcrossHierarchy(taskId, true);
                    }
                }

                // Log activities for changed fields
                const userStore = useUserStore();
                const userId = userStore.user?.id || 'unknown';
                const activityStore = useActivityStore();
                
                try {
                    // Log one activity per changed field
                    const changedFields = Object.keys(data) as Array<keyof Partial<Task>>;
                    
                    for (const field of changedFields) {
                        if (field === 'updatedAt') continue; // Skip metadata fields
                        
                        const previousValue = currentTask ? (currentTask[field] ?? 'unset') : 'unset';
                        const newValue = data[field] ?? 'unset';
                        
                        // Only log if value actually changed
                        if (previousValue !== newValue) {
                            const fieldName = field.charAt(0).toUpperCase() + field.slice(1);
                            
                            let action: 'update' | 'status_change' | 'assign' = 'update';
                            if (field === 'status') action = 'status_change';
                            else if (field === 'assignee') action = 'assign';
                            
                            await activityStore.logActivity({
                                action,
                                entityType: 'task',
                                entityId: taskId,
                                description: `Updated ${fieldName}: ${previousValue} → ${newValue}`,
                                userId,
                                userName: userStore.user?.displayName,
                                userAvatar: userStore.user?.avatarURL,
                                changedField: field as string,
                                previousValue: String(previousValue),
                                newValue: String(newValue)
                            });
                        }
                    }
                } catch (err) {
                    // Log but don't fail - activity logging is not critical
                    console.warn('Failed to log task update activity:', err);
                }

                try {
                    const nextTask = this.tasks.find(task => task.id === taskId) || { ...currentTask, ...updateData };
                    const nextAssigneeIds = this._assignedUserIds(nextTask as Partial<Task>);
                    const newlyAssignedIds = nextAssigneeIds.filter((userId) => !previousAssigneeIds.includes(userId));

                    if (newlyAssignedIds.length > 0) {
                        await this._dispatchTaskAssignedNotification(taskId, nextTask as Partial<Task>, newlyAssignedIds);
                    }

                    const shouldTriggerClientAction = (
                        (currentTask?.clientVisible !== true || currentTask?.clientResponseRequired !== true)
                        && nextTask.clientVisible === true
                        && nextTask.clientResponseRequired === true
                    );

                    if (shouldTriggerClientAction) {
                        await this._dispatchClientActionRequiredNotification(taskId, nextTask as Partial<Task>);
                    }
                } catch (notificationError) {
                    console.warn('Failed to dispatch task update notification:', notificationError);
                }

            } catch (error: any) {
                this.taskError = error.message;
                console.error('Error updating task:', error);
                throw error;
            }
        },

        /**
         * Deletes a task from Firestore
         * 
         * @param taskId - The ID of the task to delete
         * @returns {Promise<void>}
         */
        async deleteTask(taskId: string): Promise<void> {
            const { $db } = useNuxtApp();
            this.taskError = null;

            try {
                const tenantId = this._getTenantId();
                const docRef = doc($db, `tenants/${tenantId}/tasks/${taskId}`);
                
                // Get task data before deletion for activity log
                const taskToDelete = this.tasks.find(t => t.id === taskId);
                
                await deleteDoc(docRef);
                
                // Remove from local state
                this.tasks = this.tasks.filter(t => t.id !== taskId);

                try {
                    await useEntityTagsStore().removeEntityTags('task', taskId);
                } catch (tagError) {
                    console.warn('Failed to remove task tags after deletion:', tagError);
                }

                // Log activity
                const userStore = useUserStore();
                const userId = userStore.user?.id || 'unknown';
                const activityStore = useActivityStore();
                
                try {
                    await activityStore.logActivity({
                        action: 'delete',
                        entityType: 'task',
                        entityId: taskId,
                        description: `Deleted task: ${taskToDelete?.title || 'Unknown'}`,
                        userId,
                        userName: userStore.user?.displayName,
                        userAvatar: userStore.user?.avatarURL,
                        changedField: undefined
                    });
                } catch (err) {
                    // Log but don't fail - activity logging is not critical
                    console.warn('Failed to log task deletion activity:', err);
                }
            } catch (error: any) {
                this.taskError = error.message;
                console.error('Error deleting task:', error);
                throw error;
            }
        }

    }
})
